import { useState } from 'react'
import { CheckCheck, ExternalLink, Copy, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useAuth } from '@/auth/context/AuthContext'
import { useReporteWhatsAppPorCampania } from '@/modules/cronograma/hooks/useReportes'

interface WhatsAppPreviewModalProps {
  campaniaId: number | null
  nombre: string
  open: boolean
  onClose: () => void
}

export function WhatsAppPreviewModal({ campaniaId, nombre, open, onClose }: WhatsAppPreviewModalProps) {
  const { user } = useAuth()
  const { data: reporte, isLoading } = useReporteWhatsAppPorCampania(open ? campaniaId : null, open)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (!reporte?.mensaje_texto) return
    const plainText = reporte.mensaje_texto
      .replace(/\{\{nombre\}\}/gi, user?.first_name || 'Usuario')
      .replace(/\{nombre\}/gi, user?.first_name || 'Usuario')
    const textarea = document.createElement('textarea')
    textarea.value = plainText
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatText = (text: string) => {
    return text
      .replace(/\{\{nombre\}\}/gi, user?.first_name || 'Usuario')
      .replace(/\{nombre\}/gi, user?.first_name || 'Usuario')
      .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-3">
          <div className="flex items-center gap-2">
            <DialogTitle className="truncate">Vista Previa WhatsApp - {nombre}</DialogTitle>
            {reporte?.mensaje_texto && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="p-4 space-y-3">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : reporte && (reporte.mensaje_texto || reporte.imagen_url) ? (
            <div
              className="p-4 flex items-center justify-center min-h-[300px]"
              style={{ backgroundColor: '#efeae2' }}
            >
              <div className="flex items-center gap-1.5">
                <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm" style={{ maxWidth: '320px' }}>
                  {reporte.imagen_url && (
                    <div className="px-1.5 pt-1.5">
                      <img
                        src={reporte.imagen_url}
                        alt="Imagen del mensaje"
                        className="w-full h-auto max-h-[300px] object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {(reporte.mensaje_texto || reporte.footer) && (
                    <div className="px-3 py-2">
                      {reporte.mensaje_texto && (
                        <p
                          className="text-[13px] whitespace-pre-wrap text-gray-900 dark:text-gray-100 leading-snug"
                          dangerouslySetInnerHTML={{ __html: formatText(reporte.mensaje_texto) }}
                        />
                      )}
                      {reporte.footer && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{reporte.footer}</p>
                      )}
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-[10px] text-gray-500">10:30 a.m.</span>
                        <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                    </div>
                  )}

                  {reporte.botones_cta && reporte.botones_cta.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {reporte.botones_cta.map((boton, index) => (
                        <a
                          key={index}
                          href={boton.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[13px] font-medium text-[#00a884] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            index < reporte.botones_cta!.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                          }`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {boton.texto}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {/* Emoji icon */}
                <div className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 shadow-sm flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
              No hay contenido de WhatsApp para mostrar
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
